import React, { useState, useEffect, useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Globe } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DomainSelector = ({ onDomainChange, className = '' }) => {
  const { token } = useAuth();
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [loading, setLoading] = useState(true);
  const onDomainChangeRef = useRef(onDomainChange);
  
  // Keep ref up to date
  useEffect(() => {
    onDomainChangeRef.current = onDomainChange;
  }, [onDomainChange]);

  useEffect(() => {
    const fetchDomains = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/configs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const domainList = response.data.domains || [];
        setDomains(domainList);
        
        // Set default to active domain or first one
        if (domainList.length > 0) {
          const activeDomain = domainList.find(d => d.is_active) || domainList[0];
          setSelectedDomain(activeDomain.id);
          // Use setTimeout to ensure state updates before callback
          setTimeout(() => {
            onDomainChangeRef.current?.(activeDomain.id);
          }, 0);
        }
      } catch (error) {
        console.error('Failed to fetch domains:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDomains();
  }, [token]);

  const handleDomainChange = (domainId) => {
    setSelectedDomain(domainId);
    onDomainChange?.(domainId);
    
    // Also update active domain on backend
    axios.put(`${API}/config/${domainId}/activate`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(err => console.error('Failed to set active domain:', err));
  };

  // Don't show selector UI if only one domain, but still trigger the callback
  if (loading) {
    return null;
  }
  
  // If only one domain, don't show the selector but still provide the domain name
  if (domains.length === 1) {
    return (
      <div className={`flex items-center gap-2 text-sm text-slate-600 ${className}`}>
        <Globe className="w-4 h-4 text-slate-500" />
        <span className="font-medium">{domains[0].domain_name || domains[0].wp_url?.replace('https://', '').replace('http://', '')}</span>
      </div>
    );
  }
  
  // No domains configured
  if (domains.length === 0) {
    return null;
  }

  const selectedDomainData = domains.find(d => d.id === selectedDomain);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Globe className="w-4 h-4 text-slate-500" />
      <Select value={selectedDomain} onValueChange={handleDomainChange}>
        <SelectTrigger className="w-[220px] h-9 text-sm">
          <SelectValue placeholder="Select domain">
            {selectedDomainData?.domain_name || 'Select domain'}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {domains.map((domain) => (
            <SelectItem key={domain.id} value={domain.id}>
              <div className="flex flex-col">
                <span className="font-medium">{domain.domain_name || domain.wp_url}</span>
                {domain.domain_name && (
                  <span className="text-xs text-slate-500 truncate max-w-[180px]">
                    {domain.wp_url.replace('https://', '').replace('http://', '')}
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default DomainSelector;
